// Sesiones nacidas de un enlace de recuperación de contraseña.
//
// Movido tal cual desde src/lib/error-utils.ts (LDT-718): aquel módulo importa
// Sentry, y el banner de cookies de la landing solo necesita
// `isRecoveryAccessToken`. error-utils lo reexporta.

import { safeLocalStorage } from "./safe-storage";

type JwtPayload = {
  amr?: Array<{ method?: string }>;
  session_id?: string;
  exp?: number;
};

function decodeJwtPayload(accessToken: string | undefined | null): JwtPayload | null {
  if (!accessToken) return null;
  try {
    const parts = accessToken.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    return JSON.parse(atob(padded)) as JwtPayload;
  } catch {
    return null;
  }
}

// Marcador propio de "esta sesión nació de un enlace de recuperación".
//
// No se puede deducir del token. GoTrue emite `amr: [{method: "otp"}]` para los
// enlaces de recuperación, NO "recovery" — comprobado en `auth.mfa_amr_claims`
// de staging: las dos sesiones creadas por `/verify?type=recovery` (17-ago-2026)
// quedaron con `authentication_method = 'otp'`. Durante meses esta función
// devolvió `false` para TODA sesión de recovery real, así que ningún rescate
// por sesión persistida llegó a funcionar nunca.
//
// Y "otp" a secas tampoco sirve como señal: los magic links del alta
// (`billing-webhook`) producen exactamente el mismo método. Tratar una sesión
// de magic link como recovery dejaría al cliente recién pagado sin panel,
// porque AuthContext no le cargaría ni perfil ni rol.
//
// Así que la señal la ponemos nosotros, atada al `session_id` del JWT: es único
// por sesión, de modo que un login normal posterior nunca puede confundirse con
// una recuperación anterior.
const RECOVERY_SESSIONS_KEY = "playium.recovery_sessions";

function readRecoveryMarkers(): Record<string, number> {
  if (typeof window === "undefined") return {};
  try {
    const raw = safeLocalStorage.getItem(RECOVERY_SESSIONS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    const now = Math.floor(Date.now() / 1000);
    // Se podan al leer: sin esto la lista crecería sin límite y una sesión
    // caducada seguiría contando como recovery.
    return Object.fromEntries(Object.entries(parsed).filter(([, exp]) => exp > now));
  } catch {
    return {};
  }
}

/** Anota que la sesión de este token nació de un enlace de recuperación. */
export function markRecoverySession(accessToken: string | undefined | null): void {
  if (typeof window === "undefined") return;
  const payload = decodeJwtPayload(accessToken);
  if (!payload?.session_id || !payload.exp) return;
  try {
    const markers = readRecoveryMarkers();
    markers[payload.session_id] = payload.exp;
    safeLocalStorage.setItem(RECOVERY_SESSIONS_KEY, JSON.stringify(markers));
  } catch {
    // localStorage lleno o bloqueado: se pierde el rescate, no el flujo — el
    // formulario ya está en pantalla con la sesión recién establecida.
  }
}

/** Olvida el marcador de una sesión (tras cambiar la contraseña). */
export function clearRecoverySession(accessToken: string | undefined | null): void {
  if (typeof window === "undefined") return;
  const payload = decodeJwtPayload(accessToken);
  try {
    const markers = readRecoveryMarkers();
    if (payload?.session_id) delete markers[payload.session_id];
    safeLocalStorage.setItem(RECOVERY_SESSIONS_KEY, JSON.stringify(markers));
  } catch {
    // no-op
  }
}

/**
 * Comprueba si un access token de Supabase corresponde a una sesión de
 * "password recovery".
 *
 * Lo necesitamos para que la app no trate una sesión persistida de recovery
 * como una sesión de usuario normal: si el padre abre el link de reset, navega
 * a "Política de cookies" y vuelve atrás, el URL ya no tiene `?reset=1` pero
 * la sesión sigue activa en localStorage: sin este chequeo, la app le metía
 * directo al home logueado.
 *
 * Dos señales, en este orden:
 *  1. `amr.method === "recovery"`, por si GoTrue lo emite así en algún momento
 *     o en otro proyecto — es la comprobación que había, y no estorba.
 *  2. El marcador propio por `session_id` (ver arriba), que es el que funciona
 *     hoy de verdad.
 */
export function isRecoveryAccessToken(accessToken: string | undefined | null): boolean {
  const payload = decodeJwtPayload(accessToken);
  if (!payload) return false;
  if (Array.isArray(payload.amr) && payload.amr.some((m) => m?.method === "recovery")) {
    return true;
  }
  if (!payload.session_id) return false;
  return payload.session_id in readRecoveryMarkers();
}
